/**
 * A vetted recommendation's text: assembled from a resolved library entry
 * and the care manager's curation, and kept in step with the client's
 * pronoun choice after it has been added to a report or a letter.
 *
 * The library resolves names and pronouns on the server, so an added
 * recommendation is plain text worded with one pronoun choice. Each one
 * therefore carries its source: the entry it came from (topic_slug +
 * entry_ordinal), how it was curated, and the choice it was worded with.
 * When the choice in effect changes, useReresolvedPicks fetches the library
 * again under the new choice and re-assembles each pick from its own entry.
 * A pick it cannot re-word keeps its words and is marked stale for that
 * choice, never current.
 */

import { useEffect, useLayoutEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  aiToolsApi,
  type RecommendationCuration,
  type RecommendationItem,
  type RecommendationRef,
  type RecommendationsResponse,
  type SpicePronounChoice,
} from '@/lib/api';
import { pronounChoiceKey } from '@/components/aiTools/AssessmentClientBlock';

export type { RecommendationCuration, RecommendationRef } from '@/lib/api';

/** What an added recommendation needs to be re-worded later. */
export interface RecommendationSource {
  ref: RecommendationRef;
  curation: RecommendationCuration;
  /** The pronoun choice the library was resolved with for this text. */
  worded_with: SpicePronounChoice;
}

/** The library query's cache lifetime; the picker and the re-wording share it. */
export const RECOMMENDATIONS_STALE_MS = 5 * 60 * 1000;

/** How long the pronoun choice must stay unchanged before added picks are
 *  re-worded, so typing a custom set fetches once. */
export const REWORD_SETTLE_MS = 300;

/** The one query key for the library under a name and a pronoun choice. */
export function recommendationsQueryKey(
  tenantSlug: string,
  clientFirstName: string,
  pronouns: SpicePronounChoice,
): readonly unknown[] {
  return ['ai-tools', 'recommendations', tenantSlug, clientFirstName, JSON.stringify(pronouns)];
}

// A closer line such as Driver Evaluation's is a lead sentence followed by
// `- option` lines. Parse it so multi-option (D4) closers render as a
// multi-select; single closers stay a one-line include toggle.
export interface ParsedCloser {
  lead: string;
  options: string[];
}

export function parseCloser(closerText: string): ParsedCloser {
  const lines = closerText.split('\n');
  const options: string[] = [];
  const leadLines: string[] = [];
  let seenOption = false;
  for (const raw of lines) {
    const line = raw.trim();
    if (line.startsWith('-')) {
      seenOption = true;
      const opt = line.replace(/^-\s*/, '').trim();
      if (opt.length > 0) options.push(opt);
    } else if (!seenOption) {
      leadLines.push(raw);
    } else if (line.length > 0) {
      // Trailing prose after the option block (rare) - fold back into lead so
      // it is never silently dropped from the copy payload.
      leadLines.push(raw);
    }
  }
  return { lead: leadLines.join('\n').trim(), options };
}

/** How many closer options the curation chooses among (0 unless 2 or more). */
function closerOptionCount(item: RecommendationItem): number {
  if (!item.closer_text) return 0;
  const count = parseCloser(item.closer_text).options.length;
  return count >= 2 ? count : 0;
}

/**
 * The text a curated entry adds to the report: the body, then the chosen
 * seeded bullets and the care manager's own bullets (verbatim), then the
 * closer (the chosen options under their lead, or the single closer line
 * when it is included).
 */
export function assembleRecommendationText(
  item: RecommendationItem,
  curation: RecommendationCuration,
): string {
  const closer = item.closer_text ? parseCloser(item.closer_text) : null;
  const isMultiOption = !!closer && closer.options.length >= 2;
  const parts: string[] = [item.body.trim()];

  const bullets = [
    ...item.sub_items.filter((_, i) => curation.sub_selected[i]),
    ...curation.extra_items,
  ];
  if (bullets.length > 0) {
    parts.push(bullets.map((b) => `• ${b}`).join('\n'));
  }

  if (closer) {
    if (isMultiOption) {
      const chosen = closer.options.filter((_, i) => curation.options_selected[i]);
      if (chosen.length > 0) {
        const lead = closer.lead ? `${closer.lead}\n` : '';
        parts.push(`${lead}${chosen.map((o) => `• ${o}`).join('\n')}`);
      } else if (closer.lead) {
        parts.push(closer.lead);
      }
    } else if (curation.include_closer) {
      parts.push(item.closer_text!.trim());
    }
  }

  return parts.join('\n\n');
}

/**
 * True when the curation still fits the entry: one sub-item choice per
 * seeded sub-item, and one option choice per closer option. A curation that
 * no longer fits cannot say what to include, so the pick is not re-worded.
 */
export function canReresolve(item: RecommendationItem, curation: RecommendationCuration): boolean {
  return (
    curation.sub_selected.length === item.sub_items.length &&
    curation.options_selected.length === closerOptionCount(item)
  );
}

/** An added recommendation as both pages hold it. */
export interface ReresolvablePick {
  id: string;
  text: string;
  ref?: RecommendationRef;
  curation?: RecommendationCuration;
  worded_with?: SpicePronounChoice;
  /** The choice key for which re-wording failed; the pick keeps its words. */
  stale_for?: string;
}

/**
 * A pick waiting to be re-worded for the choice key: it has a source, was
 * worded with another choice, and has not already failed for this one. A
 * pick without a ref (added before sources were recorded) is never pending.
 */
function isPendingFor(pick: ReresolvablePick, key: string): boolean {
  return pick.ref !== undefined && pronounChoiceKey(pick.worded_with) !== key && pick.stale_for !== key;
}

/** A pick whose re-wording failed for the choice now in effect. */
export function isPickStale(pick: ReresolvablePick, currentKey: string): boolean {
  return pick.ref !== undefined && pick.stale_for !== undefined && pick.stale_for === currentKey;
}

function entryFor(items: readonly RecommendationItem[], ref: RecommendationRef): RecommendationItem | undefined {
  return items.find((it) => it.topic_slug === ref.topic_slug && it.entry_ordinal === ref.entry_ordinal);
}

/** The pick after a library response for `choice`: re-worded, stale, or as it was. */
function reworded<P extends ReresolvablePick>(
  pick: P,
  items: readonly RecommendationItem[],
  choice: SpicePronounChoice,
  key: string,
): P {
  if (pick.ref === undefined || !isPendingFor(pick, key)) return pick;
  const item = entryFor(items, pick.ref);
  if (item === undefined || pick.curation === undefined || !canReresolve(item, pick.curation)) {
    return { ...pick, stale_for: key };
  }
  return {
    ...pick,
    text: assembleRecommendationText(item, pick.curation),
    worded_with: choice,
    stale_for: undefined,
  };
}

export interface UseReresolvedPicksOptions<P extends ReresolvablePick> {
  tenantSlug: string;
  /** The client first name the library resolves with (read when a fetch starts). */
  firstName: string;
  /** The pronoun choice now in effect; undefined while the page has none. */
  effectiveChoice: SpicePronounChoice | undefined;
  picks: readonly P[];
  setPicks: (update: (prev: P[]) => P[]) => void;
  /** Called when a response re-words at least one pick. */
  onRewrite?: () => void;
}

export interface ReresolvedPicks {
  /** True while any pick waits to be re-worded for the choice in effect. */
  picksPending: boolean;
  /** pronounChoiceKey of the choice in effect. */
  currentKey: string;
  /** Clear a pick's stale mark so it is re-worded again. */
  retry: (id: string) => void;
}

/**
 * Keep added picks worded with the pronoun choice in effect.
 *
 * picksPending is derived on every render, so Save and Generate are disabled
 * from the render in which the choice changes. REWORD_SETTLE_MS after the
 * choice last changed, the library is requested through the query cache
 * under the picker's own key, so an open picker's answer for the same choice
 * is reused rather than asked for twice: its data while fresh, and its
 * failure when it failed after these picks began waiting (one request, one
 * outcome on the page). A response is applied only while its choice is
 * still the one in effect. For each pending pick, a matching entry whose
 * curation still fits re-words the text and records the choice fetched; a
 * missing entry, a curation that no longer fits, or a failed request keeps
 * the words and marks the pick stale for that choice. Try again clears the
 * mark and requests afresh.
 */
export function useReresolvedPicks<P extends ReresolvablePick>({
  tenantSlug,
  firstName,
  effectiveChoice,
  picks,
  setPicks,
  onRewrite,
}: UseReresolvedPicksOptions<P>): ReresolvedPicks {
  const queryClient = useQueryClient();
  const currentKey = pronounChoiceKey(effectiveChoice);
  const pendingIds =
    effectiveChoice === undefined ? [] : picks.filter((p) => isPendingFor(p, currentKey)).map((p) => p.id);
  const picksPending = pendingIds.length > 0;
  const pendingSignature = pendingIds.join('\n');

  // The latest committed values, read when a request starts or a response
  // arrives. A response for a choice other than the current one is dropped.
  const latest = useRef({ currentKey, effectiveChoice, firstName, picks, setPicks, onRewrite });
  useLayoutEffect(() => {
    latest.current = { currentKey, effectiveChoice, firstName, picks, setPicks, onRewrite };
  });

  useEffect(() => {
    if (pendingSignature === '') return;
    // When these picks began waiting for this choice.
    const waitingSince = Date.now();
    const run = (): void => {
      const { effectiveChoice: choice, firstName: name } = latest.current;
      if (choice === undefined) return;
      const key = pronounChoiceKey(choice);
      const clientFirstName = name.trim();
      const queryKey = recommendationsQueryKey(tenantSlug, clientFirstName, choice);
      const cached = queryClient.getQueryState(queryKey);
      const request: Promise<RecommendationsResponse> =
        cached?.status === 'error' && cached.fetchStatus === 'idle' && cached.errorUpdatedAt >= waitingSince
          ? Promise.reject(cached.error)
          : queryClient.fetchQuery({
              queryKey,
              queryFn: () =>
                aiToolsApi.listRecommendations(tenantSlug, {
                  client_first_name: clientFirstName,
                  pronouns: choice,
                }),
              staleTime: RECOMMENDATIONS_STALE_MS,
            });
      request.then(
        (res) => {
          const now = latest.current;
          if (now.currentKey !== key) return;
          const rewrites = now.picks.some((p) => {
            if (p.ref === undefined || p.curation === undefined || !isPendingFor(p, key)) return false;
            const item = entryFor(res.items, p.ref);
            return item !== undefined && canReresolve(item, p.curation);
          });
          now.setPicks((prev) => prev.map((p) => reworded(p, res.items, choice, key)));
          if (rewrites) now.onRewrite?.();
        },
        () => {
          const now = latest.current;
          if (now.currentKey !== key) return;
          now.setPicks((prev) => prev.map((p) => (isPendingFor(p, key) ? { ...p, stale_for: key } : p)));
        },
      );
    };
    const timer = setTimeout(run, REWORD_SETTLE_MS);
    return () => clearTimeout(timer);
  }, [currentKey, pendingSignature, tenantSlug, queryClient]);

  const retry = (id: string): void => {
    setPicks((prev) => prev.map((p) => (p.id === id ? { ...p, stale_for: undefined } : p)));
  };

  return { picksPending, currentKey, retry };
}
